import { useCallback, useEffect, useRef, useState } from 'react';
import {
  API_ORIGIN,
  normalizeISO2Country,
  normalizeSignKey,
} from '../utils/homeHelpers';
import {
  TRIVIA_LEADERBOARD_CACHE_KEY,
  createTriviaLeaderboardCacheKey,
  isActiveTriviaLeaderboardViewRequest,
  isCurrentTriviaLeaderboardCacheKey,
  normalizeCompetitiveLeaderboardPayload,
} from '../utils/triviaLeaderboard';
import { _todayKey } from '../utils/triviaHelpers';

const LB_FETCH_TIMEOUT_MS = 2500;

const useLeaderboard = ({ user, resolvedUidLb, showTrivia }) => {
  const [lbScope, setLbScope] = useState('global');
  const [lbPeriod, setLbPeriod] = useState('month');
  const [lbItems, setLbItems] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbMyRank, setLbMyRank] = useState(null);
  const [lbMyScore, setLbMyScore] = useState(null);
  const [lbMyDisplayName, setLbMyDisplayName] = useState(null);
  const [lbMyAvatarPath, setLbMyAvatarPath] = useState(null);
  const [lbMyMetrics, setLbMyMetrics] = useState(null);
  const [lbMonthKey, setLbMonthKey] = useState(null);
  const [lbError, setLbError] = useState(false);
  const [lbHydrated, setLbHydrated] = useState(false);

  const lbCacheRef = useRef({});
  const lbInflightRef = useRef({});
  const lbLastFetchRef = useRef({});
  const lbActiveViewKeyRef = useRef(null);

  const [countryCode, setCountryCode] = useState(() => (
    normalizeISO2Country(
      localStorage.getItem('country') ||
      user?.country ||
      user?.countryCode ||
      ''
    )
  ));

  const [lbSign, setLbSign] = useState(() => normalizeSignKey(user?.zodiacSign || ''));

  useEffect(() => {
    setLbHydrated(true);
  }, []);

  useEffect(() => {
    setLbSign(normalizeSignKey(user?.zodiacSign || ''));
  }, [user?.zodiacSign]);

  useEffect(() => {
    const cc = normalizeISO2Country(user?.country || user?.countryCode || '');
    if (cc && cc !== countryCode) setCountryCode(cc);
  }, [user?.country, user?.countryCode, countryCode]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_ORIGIN}/api/whereami`, { credentials: 'include' });
        if (!res.ok) return;

        const j = await res.json().catch(() => null);
        const cc = normalizeISO2Country(j?.country);

        if (cc && !cancelled) {
          localStorage.setItem('country', cc);
          setCountryCode(cc);
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const resetLeaderboardRuntimeCache = useCallback(() => {
    try { lbInflightRef.current = {}; } catch {}
    try { lbLastFetchRef.current = {}; } catch {}
  }, []);

  const fetchLeaderboard = useCallback(
    async (
      scope = 'global',
      signOverride = null,
      { preferCacheFirst = true, viewScope = null, period = 'month', viewPeriod = null } = {}
    ) => {
      const cc = (
        countryCode ||
        localStorage.getItem('country') ||
        user?.country ||
        user?.countryCode ||
        ''
      );
      const normalizedCountry = normalizeISO2Country(cc);

      const rawSign = signOverride || lbSign || (user?.zodiacSign || '');
      const normSign = normalizeSignKey(rawSign);

      const effectiveScope = (scope === 'country' && !/^[A-Z]{2}$/.test(normalizedCountry)) ? 'global' : scope;
      const cacheDay = period === 'today' ? _todayKey() : '';

      const key = createTriviaLeaderboardCacheKey({
        scope: effectiveScope,
        sign: normSign,
        country: normalizedCountry,
        userId: resolvedUidLb || '',
        period,
        day: cacheDay,
      });
      // Calls originating from a visible scope/period control own the rendered
      // view. Background prefetches may populate cache but must never replace
      // a newer user-selected leaderboard when they resolve out of order.
      const isVisibleViewRequest = viewScope !== null || viewPeriod !== null;
      if (isVisibleViewRequest) lbActiveViewKeyRef.current = key;
      const ownsVisibleView = () => isActiveTriviaLeaderboardViewRequest(lbActiveViewKeyRef.current, key);

      const now = Date.now();
      const lastTs = lbLastFetchRef.current[key] || 0;

      if (now - lastTs < 2000) {
        return;
      }

      lbLastFetchRef.current[key] = now;

      const cached = lbCacheRef.current[key];

      if (preferCacheFirst && cached) {
        const safeCached = normalizeCompetitiveLeaderboardPayload(cached, period, resolvedUidLb);
        if (ownsVisibleView()) {
          setLbItems(safeCached.items);
          setLbMyRank(safeCached.myRank);
          setLbMyScore(safeCached.myScore);
          setLbMyDisplayName(safeCached.myDisplayName ?? null);
          setLbMyAvatarPath(safeCached.myAvatarPath ?? null);
          setLbMyMetrics(safeCached.myMetrics ?? null);
          setLbMonthKey(safeCached.monthKey ?? null);
          setLbError(false);
          setLbLoading(false);
        }
      } else {
        if (ownsVisibleView()) {
          // Never render Month rows as Today while a day-scoped request loads.
          setLbItems([]);
          setLbMyRank(null);
          setLbMyScore(null);
          setLbMyDisplayName(null);
          setLbMyAvatarPath(null);
          setLbMyMetrics(null);
          setLbMonthKey(null);
          setLbError(false);
          setLbLoading(true);
        }
      }

      if (lbInflightRef.current[key]) return;
      lbInflightRef.current[key] = true;

      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), LB_FETCH_TIMEOUT_MS);

      try {
        const params = new URLSearchParams({
          scope: String(effectiveScope),
          limit: '10',
        });

        if (effectiveScope === 'sign') {
          if (normSign) params.set('sign', normSign);
        } else if (effectiveScope === 'country') {
          if (normalizedCountry) params.set('country', normalizedCountry);
        }
        if (resolvedUidLb) params.set('userId', String(resolvedUidLb));

        params.set('period', period);

        const url = `${API_ORIGIN}/api/trivia/leaderboard?${params.toString()}`;
        const res = await fetch(url, {
          credentials: 'include',
          signal: ctrl.signal,
        });

        const json = await res.json().catch(() => ({ ok: false }));

        if (json && json.ok) {
          setLbError(false);
          const payload = normalizeCompetitiveLeaderboardPayload({
            ts: Date.now(),
            day: json.day ?? null,
            items: Array.isArray(json.items) ? json.items : [],
            myRank: json.myRank ?? null,
            myScore: json.myScore ?? null,
            myDisplayName: json.myDisplayName ?? null,
            myAvatarPath: json.myAvatarPath ?? null,
            myMetrics: json.myMetrics ?? null,
            monthKey: json.monthKey ?? null,
          }, period, resolvedUidLb);

          const mayUpdateVisibleView = ownsVisibleView();
          if (mayUpdateVisibleView) {
            setLbItems(payload.items);
            setLbMyRank(payload.myRank);
            setLbMyScore(payload.myScore);
            setLbMyDisplayName(payload.myDisplayName);
            setLbMyAvatarPath(payload.myAvatarPath);
            setLbMyMetrics(payload.myMetrics);
            setLbMonthKey(payload.monthKey);
            setLbLoading(false);
          }

          lbCacheRef.current[key] = payload;

          try {
            const raw = localStorage.getItem(TRIVIA_LEADERBOARD_CACHE_KEY);
            const disk = raw ? JSON.parse(raw) : {};
            disk[key] = payload;

            const entries = Object.entries(disk);

            if (entries.length > 12) {
              entries.sort((a, b) => (a[1]?.ts || 0) - (b[1]?.ts || 0));
              while (entries.length > 12) entries.shift();
            }

            localStorage.setItem(TRIVIA_LEADERBOARD_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
          } catch {}
        } else {
          if (!cached && ownsVisibleView()) setLbError(true);
          if (ownsVisibleView()) setLbLoading(false);
        }
      } catch {
        if (!cached && ownsVisibleView()) setLbError(true);
        if (ownsVisibleView()) setLbLoading(false);
      } finally {
        clearTimeout(to);
        lbInflightRef.current[key] = false;
      }
    },
    [
      countryCode,
      user?.country,
      user?.countryCode,
      lbSign,
      user?.zodiacSign,
      resolvedUidLb,
    ]
  );

  useEffect(() => {
    if (!resolvedUidLb) return;
    if (showTrivia) return;
    if (window.__lbPrefetchDone) return;

    window.__lbPrefetchDone = true;

    fetchLeaderboard('global', null, { preferCacheFirst: true, period: 'month' });

    const timers = [];

    timers.push(setTimeout(() => {
      fetchLeaderboard('country', null, { preferCacheFirst: true, period: 'month' });
    }, 2000));

    timers.push(setTimeout(() => {
      fetchLeaderboard('sign', null, { preferCacheFirst: true, period: 'month' });
    }, 4000));

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [resolvedUidLb, showTrivia, fetchLeaderboard]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TRIVIA_LEADERBOARD_CACHE_KEY);
      if (!raw) return;

      const disk = JSON.parse(raw);

      if (disk && typeof disk === 'object') {
        const todayDayKey = _todayKey();
        const currentEntries = Object.entries(disk).filter(([key]) => (
          isCurrentTriviaLeaderboardCacheKey(key, todayDayKey)
        ));
        const currentDisk = Object.fromEntries(currentEntries);
        lbCacheRef.current = currentDisk;

        // V3 daily entries have no calendar-day identity. Discard them and
        // any previous-day V4 daily entry rather than showing it at rollover.
        if (currentEntries.length !== Object.keys(disk).length) {
          localStorage.setItem(TRIVIA_LEADERBOARD_CACHE_KEY, JSON.stringify(currentDisk));
        }

        if (!lbItems || lbItems.length === 0) {
          let newestPayload = null;
          let newestPeriod = 'month';
          let newestTs = -1;

          for (const [k, p] of currentEntries) {
            if (!p || typeof p !== 'object') continue;

            const ts = Number(p.ts || 0);

            if (ts > newestTs && Array.isArray(p.items)) {
              newestTs = ts;
              newestPayload = p;
              newestPeriod = JSON.parse(k).period || 'month';
            }
          }

          if (newestPayload) {
            // A cache entry may have been written before a server rollout;
            // normalize it at the render boundary as well as on network read.
            const safePayload = normalizeCompetitiveLeaderboardPayload(newestPayload, newestPeriod, resolvedUidLb);
            setLbItems(safePayload.items);
            setLbMyRank(safePayload.myRank);
            setLbMyScore(safePayload.myScore);
            setLbMyDisplayName(safePayload.myDisplayName ?? null);
            setLbMyAvatarPath(safePayload.myAvatarPath ?? null);
            setLbMyMetrics(safePayload.myMetrics ?? null);
            setLbMonthKey(safePayload.monthKey ?? null);
            setLbError(false);
            setLbLoading(false);
          }
        }
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    lbScope,
    setLbScope,
    lbPeriod,
    setLbPeriod,
    lbItems,
    lbLoading,
    lbMyRank,
    lbMyScore,
    lbMyDisplayName,
    lbMyAvatarPath,
    lbMyMetrics,
    lbMonthKey,
    lbError,
    lbHydrated,
    lbSign,
    setLbSign,
    countryCode,
    setCountryCode,
    fetchLeaderboard,
    resetLeaderboardRuntimeCache,
  };
};

export default useLeaderboard;
